/**
 * DashboardSettingsContext
 * 
 * React Context for managing Live Location Activity Dashboard settings.
 * Provides global settings with localStorage persistence and reset functionality.
 * 
 * Features:
 * - Carousel settings (dwell time, queue size, merge, transitions)
 * - Cache settings (size, persistence, midnight reset)
 * - Heatmap settings (time window, update interval, color scheme)
 * - Display settings (auto-switch, indicators, panels, animation speed)
 * - localStorage persistence for all settings
 * - Reset to default values
 * - Type-safe settings management
 * 
 * @author BizClick WMS
 * @version 1.31 - Fase 8 Implementation
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as localStorageHelper from '../utils/localStorageHelper';

/**
 * Animation speed options
 */
export type AnimationSpeed = 'slow' | 'normal' | 'fast';

/**
 * Heatmap color scheme options
 */
export type ColorScheme = 'cold-hot' | 'green-red' | 'grayscale';

/**
 * Time window options for heatmap
 */
export type TimeWindowOption = '1h' | '4h' | '8h' | 'today';

/**
 * Dashboard settings interface
 */
export interface DashboardSettings {
  // Carousel Settings
  carousel: {
    dwellTime: number; // milliseconds (5000-30000)
    maxQueueSize: number; // items (10-100)
    mergeSameRack: boolean;
    transitionDuration: number; // milliseconds (200-1000)
  };

  // Cache Settings
  cache: {
    maxCacheSize: number; // events (5000-20000)
    persistToLocalStorage: boolean;
    resetAtMidnight: boolean;
  };

  // Heatmap Settings
  heatmap: {
    defaultTimeWindow: TimeWindowOption;
    updateInterval: number; // seconds (10-60)
    colorScheme: ColorScheme;
  };

  // Display Settings
  display: {
    autoSwitchEnabled: boolean;
    showQueueIndicator: boolean;
    showStatisticsPanel: boolean;
    animationSpeed: AnimationSpeed;
  };
}

/**
 * Default settings
 */
export const DEFAULT_SETTINGS: DashboardSettings = {
  carousel: {
    dwellTime: 10000, // 10 seconds
    maxQueueSize: 100,
    mergeSameRack: true,
    transitionDuration: 500 // 500ms
  },
  cache: {
    maxCacheSize: 10000,
    persistToLocalStorage: true,
    resetAtMidnight: true
  },
  heatmap: {
    defaultTimeWindow: '8h',
    updateInterval: 30, // 30 seconds
    colorScheme: 'cold-hot'
  },
  display: {
    autoSwitchEnabled: true,
    showQueueIndicator: true,
    showStatisticsPanel: true,
    animationSpeed: 'normal'
  }
};

/**
 * Context value interface
 */
interface DashboardSettingsContextValue {
  settings: DashboardSettings;
  updateSettings: (updates: Partial<DashboardSettings>) => void;
  resetSettings: () => void;
  isLoading: boolean;
}

/**
 * localStorage key for settings persistence
 */
const SETTINGS_STORAGE_KEY = 'bizclick_dashboard_settings';

/**
 * Create context
 */
const DashboardSettingsContext = createContext<DashboardSettingsContextValue | undefined>(undefined);

/**
 * Provider props
 */
interface DashboardSettingsProviderProps {
  children: ReactNode;
}

/**
 * Dashboard Settings Provider
 */
export const DashboardSettingsProvider: React.FC<DashboardSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<DashboardSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load settings from localStorage on mount
   */
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = localStorageHelper.getItem<DashboardSettings>(SETTINGS_STORAGE_KEY);
        
        if (savedSettings) {
          // Merge with defaults to ensure all properties exist
          const mergedSettings: DashboardSettings = {
            carousel: { ...DEFAULT_SETTINGS.carousel, ...savedSettings.carousel },
            cache: { ...DEFAULT_SETTINGS.cache, ...savedSettings.cache },
            heatmap: { ...DEFAULT_SETTINGS.heatmap, ...savedSettings.heatmap },
            display: { ...DEFAULT_SETTINGS.display, ...savedSettings.display }
          };
          
          setSettings(mergedSettings);
          console.log('[DashboardSettings] Settings loaded from localStorage:', mergedSettings);
        } else {
          console.log('[DashboardSettings] No saved settings found, using defaults');
        }
      } catch (error) {
        console.error('[DashboardSettings] Error loading settings:', error);
        // Use defaults on error
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  /**
   * Update settings with partial updates
   */
  const updateSettings = (updates: Partial<DashboardSettings>) => {
    setSettings(prevSettings => {
      const newSettings: DashboardSettings = {
        carousel: { ...prevSettings.carousel, ...(updates.carousel || {}) },
        cache: { ...prevSettings.cache, ...(updates.cache || {}) },
        heatmap: { ...prevSettings.heatmap, ...(updates.heatmap || {}) },
        display: { ...prevSettings.display, ...(updates.display || {}) }
      };

      // Persist to localStorage
      const saved = localStorageHelper.setItem(SETTINGS_STORAGE_KEY, newSettings);
      
      if (saved) {
        console.log('[DashboardSettings] Settings updated and saved:', updates);
      } else {
        console.warn('[DashboardSettings] Failed to save settings to localStorage');
      }

      return newSettings;
    });
  };

  /**
   * Reset settings to defaults
   */
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    
    // Persist defaults to localStorage
    const saved = localStorageHelper.setItem(SETTINGS_STORAGE_KEY, DEFAULT_SETTINGS);
    
    if (saved) {
      console.log('[DashboardSettings] Settings reset to defaults');
    } else {
      console.warn('[DashboardSettings] Failed to save default settings to localStorage');
    }
  };

  const value: DashboardSettingsContextValue = {
    settings,
    updateSettings,
    resetSettings,
    isLoading
  };

  return (
    <DashboardSettingsContext.Provider value={value}>
      {children}
    </DashboardSettingsContext.Provider>
  );
};

/**
 * Custom hook to use dashboard settings
 */
export const useDashboardSettings = (): DashboardSettingsContextValue => {
  const context = useContext(DashboardSettingsContext);
  
  if (context === undefined) {
    throw new Error('useDashboardSettings must be used within a DashboardSettingsProvider');
  }
  
  return context;
};

/**
 * Get animation timing based on speed setting
 */
export const getAnimationTiming = (speed: AnimationSpeed): { pulse: number; fadeout: number } => {
  switch (speed) {
    case 'slow':
      return { pulse: 7000, fadeout: 3000 };
    case 'fast':
      return { pulse: 3000, fadeout: 1500 };
    case 'normal':
    default:
      return { pulse: 5000, fadeout: 2000 };
  }
};

/**
 * Validate settings values
 */
export const validateSettings = (settings: DashboardSettings): boolean => {
  try {
    // Validate carousel settings
    if (settings.carousel.dwellTime < 5000 || settings.carousel.dwellTime > 30000) return false;
    if (settings.carousel.maxQueueSize < 10 || settings.carousel.maxQueueSize > 100) return false;
    if (settings.carousel.transitionDuration < 200 || settings.carousel.transitionDuration > 1000) return false;

    // Validate cache settings
    if (settings.cache.maxCacheSize < 5000 || settings.cache.maxCacheSize > 20000) return false;

    // Validate heatmap settings
    if (settings.heatmap.updateInterval < 10 || settings.heatmap.updateInterval > 60) return false;
    if (!['1h', '4h', '8h', 'today'].includes(settings.heatmap.defaultTimeWindow)) return false;
    if (!['cold-hot', 'green-red', 'grayscale'].includes(settings.heatmap.colorScheme)) return false;

    // Validate display settings
    if (!['slow', 'normal', 'fast'].includes(settings.display.animationSpeed)) return false;

    return true;
  } catch {
    return false;
  }
};

export default DashboardSettingsContext;
